/**
 * PlaceStage NPR kit — painterly soft Standard + subtle warm edge.
 * Goal: Firewatch *mood* (warm haze, soft light) with readable architecture —
 * NOT bare Lambert cubes (Minecraft) and NOT hard Messenger cel.
 */
import {
  BackSide,
  Color,
  DataTexture,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  MeshToonMaterial,
  NearestFilter,
  RedFormat,
} from "three";

export type StyleMode = "firewatch" | "messenger";

/** Mutable look switch — set before loadDistrict / hero factories. */
export const styleMode: { current: StyleMode } = { current: "firewatch" };

/** Soft multi-band ramp (LinearFilter) — unused in firewatch; kept for messenger. */
export function createSoftGradient(): DataTexture {
  const bands = [70, 100, 130, 160, 190, 220, 245];
  const data = new Uint8Array(bands.length);
  for (let i = 0; i < bands.length; i++) data[i] = bands[i];
  const tex = new DataTexture(data, bands.length, 1, RedFormat);
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

/** Hard 5-band cel ramp (legacy Messenger). */
export function createToonGradient(): DataTexture {
  const bands = [40, 90, 140, 190, 235];
  const data = new Uint8Array(bands.length);
  for (let i = 0; i < bands.length; i++) data[i] = bands[i];
  const tex = new DataTexture(data, bands.length, 1, RedFormat);
  tex.minFilter = NearestFilter;
  tex.magFilter = NearestFilter;
  tex.needsUpdate = true;
  return tex;
}

export const softGradient = createSoftGradient();
export const toonGradient = createToonGradient();

export type Palette = {
  sky: number;
  fog: number;
  groundLow: number;
  groundMid: number;
  groundHigh: number;
  cliff: number;
  park: number;
  road: number;
  wall: number;
  building: number;
  buildingAccent: number;
  buildingResidential: number;
  buildingCommercial: number;
  buildingPublic: number;
  buildingCivic: number;
  buildingUtility: number;
  roof: number;
  roofCopper: number;
  roofSlate: number;
  roofFlat: number;
  water: number;
  stone: number;
  stoneDark: number;
  ink: number;
  sun: number;
  hemiSky: number;
  hemiGround: number;
};

/** Warm late-day — haze sells depth; materials carry surface. */
export const palette: Palette = {
  sky: 0xc4b6a2,
  fog: 0xd0c2ae,
  groundLow: 0x5a7a58,
  groundMid: 0x7a9268,
  groundHigh: 0xb8a878,
  cliff: 0x8a7a6a,
  park: 0x4f7a55,
  road: 0x6a645c,
  wall: 0xa89888,
  building: 0xe2d4c0,
  buildingAccent: 0xd4c4a8,
  buildingResidential: 0xe0d2bc,
  buildingCommercial: 0xd8c4a8,
  buildingPublic: 0xd4cec4,
  buildingCivic: 0xe4dcc8,
  buildingUtility: 0x9a948c,
  roof: 0x4a5c52,
  roofCopper: 0x3a6450,
  roofSlate: 0x4a4e58,
  roofFlat: 0x3a3836,
  water: 0x5a8090,
  stone: 0xcfc4b4,
  stoneDark: 0xa89e90,
  ink: 0x2e2a24,
  sun: 0xffc890,
  hemiSky: 0xe8d4b8,
  hemiGround: 0x6a6858,
};

/** Greener parks, cream walls, terracotta roofs — La Fortuna postcard. */
export function applyTropicalPalette() {
  Object.assign(palette, {
    sky: 0x9ec8e0,
    fog: 0xb8d4e4,
    groundLow: 0x4f9a5a,
    groundMid: 0x6aad62,
    groundHigh: 0xc9b48a,
    cliff: 0x7a6e62,
    park: 0x3f9a55,
    road: 0x3a3836,
    wall: 0xa89888,
    building: 0xf2e8d8,
    buildingAccent: 0xe8d8c0,
    buildingResidential: 0xf0e4d4,
    buildingCommercial: 0xe8d4bc,
    buildingPublic: 0xe4ddd4,
    buildingCivic: 0xf5efe4,
    buildingUtility: 0xa09890,
    roof: 0xb54a3a,
    roofCopper: 0xc45a3e,
    roofSlate: 0xb54a3a,
    roofFlat: 0x8a5044,
    water: 0x4a8aaa,
    stone: 0xf0e8dc,
    stoneDark: 0xb8a898,
    ink: 0x1e2830,
    sun: 0xffe8c0,
    hemiSky: 0xc8e4f4,
    hemiGround: 0x5a8a58,
  } satisfies Partial<Palette>);
}

/** Optional: hard cel + ink (old Messenger look). */
export function applyMessengerStyle() {
  styleMode.current = "messenger";
  Object.assign(palette, {
    sky: 0xb8c9d6,
    fog: 0xb8c9d6,
    groundLow: 0x6b8f6e,
    groundMid: 0x8fa37a,
    groundHigh: 0xc4b896,
    cliff: 0x7a6e62,
    park: 0x5f8f68,
    road: 0x5c5854,
    wall: 0x9a9084,
    building: 0xe8dcc8,
    buildingAccent: 0xd4c4a8,
    buildingResidential: 0xe6d7c2,
    buildingCommercial: 0xdcc4a4,
    buildingPublic: 0xd0cbc2,
    buildingCivic: 0xe8e0d0,
    buildingUtility: 0x9a948c,
    roof: 0x4a5c52,
    roofCopper: 0x3d6b55,
    roofSlate: 0x4a4e58,
    roofFlat: 0x3a3836,
    water: 0x4a7a8c,
    stone: 0xd8d0c4,
    stoneDark: 0xa89e90,
    ink: 0x1e2430,
    sun: 0xffe0b8,
    hemiSky: 0xd6e4f0,
    hemiGround: 0x6a7568,
  } satisfies Partial<Palette>);
}

export type StageMaterial = MeshStandardMaterial | MeshToonMaterial;

/**
 * Soft Standard by default — slight roughness variance via opts.
 * Copper: pass metalness ~0.35, roughness ~0.45.
 */
export function toonMat(
  color: number,
  opts?: {
    transparent?: boolean;
    opacity?: number;
    roughness?: number;
    metalness?: number;
  },
): StageMaterial {
  const transparent = opts?.transparent ?? false;
  const opacity = opts?.opacity ?? 1;
  if (styleMode.current === "messenger") {
    return new MeshToonMaterial({
      color,
      gradientMap: toonGradient,
      transparent,
      opacity,
    });
  }
  return new MeshStandardMaterial({
    color,
    roughness: opts?.roughness ?? 0.84,
    metalness: opts?.metalness ?? 0.02,
    transparent,
    opacity,
  });
}

/** Alias for hero factories — same as toonMat. */
export const stageMat = toonMat;

const outlineMat = new MeshBasicMaterial({
  color: 0x2e2a24,
  side: BackSide,
  depthWrite: false,
  transparent: true,
  opacity: 0.55,
});

/**
 * Soft warm edge — always on in firewatch (tiny inflate).
 * Messenger keeps stronger ink shells.
 */
export function withOutline(mesh: Mesh, inflate = 1.035): Mesh {
  outlineMat.color.setHex(palette.ink);
  const soft = styleMode.current === "firewatch";
  const shell = new Mesh(mesh.geometry, outlineMat);
  shell.name = "outline";
  const f = soft ? Math.min(inflate, 1.018) : inflate;
  shell.scale.set(f, soft ? 1.004 : 1.008, f);
  shell.position.y = soft ? 0.02 : 0.04;
  shell.renderOrder = -1;
  if (soft) {
    const m = outlineMat.clone();
    m.opacity = 0.4;
    shell.material = m;
  }
  mesh.add(shell);
  return mesh;
}

export function tintFogColor(): Color {
  return new Color(palette.fog);
}
